/**
 * Upcoming Bridge Sessions page: list current user's bookings, change time.
 * @feature BRIDGE_SESSION_BOOKING - List and reschedule Bridge Session bookings
 */
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, DatePicker, message, Spin, Empty, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  bridgeSessionBookingService,
  type BridgeSessionBooking,
  type BridgeSessionBookingCreateResponse,
} from '../services/bridgeSessionBookingService';
import { BridgeSessionBookingDialog } from '../components/BridgeSessionBooking/BridgeSessionBookingDialog';
import './UpcomingSessionsPage.css';

export const UpcomingSessionsPage: React.FC = () => {
  const [bookings, setBookings] = useState<BridgeSessionBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [changeTimeModalOpen, setChangeTimeModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BridgeSessionBooking | null>(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleForm] = Form.useForm<{ scheduled_at: dayjs.Dayjs }>();

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const list = await bridgeSessionBookingService.list();
      setBookings(list);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to load sessions';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleBookingComplete = async (response: BridgeSessionBookingCreateResponse) => {
    setBookingDialogOpen(false);
    if (response.access_token) {
      localStorage.setItem('token', response.access_token);
    }
    message.success('Bridge Session booked successfully');
    await fetchBookings();
  };

  const handleChangeTime = (record: BridgeSessionBooking) => {
    setSelectedBooking(record);
    rescheduleForm.setFieldsValue({
      scheduled_at: record.scheduled_at ? dayjs(record.scheduled_at) : dayjs().add(1, 'week'),
    });
    setChangeTimeModalOpen(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedBooking) return;
    try {
      const values = await rescheduleForm.validateFields();
      const scheduledAt = values.scheduled_at.toISOString();
      setRescheduleLoading(true);
      await bridgeSessionBookingService.updateScheduledAt(selectedBooking.id, scheduledAt);
      message.success('Session time updated');
      setChangeTimeModalOpen(false);
      setSelectedBooking(null);
      rescheduleForm.resetFields();
      await fetchBookings();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to update time';
      message.error(msg);
    } finally {
      setRescheduleLoading(false);
    }
  };

  const minDate = dayjs().startOf('day');
  const maxDate = dayjs().add(1, 'year');

  const columns: ColumnsType<BridgeSessionBooking> = [
    {
      title: 'Date & Time',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (val: string | undefined) => val || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => handleChangeTime(record)}>
          Change time
        </Button>
      ),
    },
  ];

  return (
    <div className="upcoming-sessions-page">
      <Card
        title="Upcoming Bridge Sessions"
        className="upcoming-sessions-card"
        extra={
          <Button type="primary" onClick={() => setBookingDialogOpen(true)}>
            Schedule another session
          </Button>
        }
      >
        {loading ? (
          <div className="upcoming-sessions-loading">
            <Spin size="large" />
          </div>
        ) : bookings.length === 0 ? (
          <Empty
            description="No upcoming sessions"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Space>
              <Button type="primary" onClick={() => setBookingDialogOpen(true)}>
                Book a Bridge Session
              </Button>
            </Space>
          </Empty>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={bookings}
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      <BridgeSessionBookingDialog
        visible={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        onComplete={(r) => void handleBookingComplete(r)}
      />

      <Modal
        title="Change session time"
        open={changeTimeModalOpen}
        onCancel={() => {
          setChangeTimeModalOpen(false);
          setSelectedBooking(null);
          rescheduleForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={rescheduleForm}
          layout="vertical"
          onFinish={handleRescheduleSubmit}
        >
          <Form.Item
            label="New date & time"
            name="scheduled_at"
            rules={[{ required: true, message: 'Please select date and time' }]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
              minDate={minDate}
              maxDate={maxDate}
              disabledDate={(d: dayjs.Dayjs | null) => d != null && (d.isBefore(minDate) || d.isAfter(maxDate))}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={rescheduleLoading}
              style={{ marginRight: 8 }}
            >
              Update time
            </Button>
            <Button
              onClick={() => {
                setChangeTimeModalOpen(false);
                setSelectedBooking(null);
                rescheduleForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UpcomingSessionsPage;
